<script setup lang="ts">
import { computed, ref } from 'vue'
import { useDark, useToggle } from '@vueuse/core'
import { useRouter } from 'vue-router'

import Dialog from 'primevue/dialog'
import { useConfirm } from 'primevue/useconfirm'

import { useI18n } from 'vue-i18n'

import AppAvatar from '@/components/atoms/avatar/AppAvatar.vue'
import { useAuthStore } from '@/stores/useAuthStore'
import { useI18nStore } from '@/stores/useI18nStore'

const { t, locale } = useI18n()
const authStore = useAuthStore()
const i18nStore = useI18nStore()
const router = useRouter()
const isDark = useDark({ selector: 'html' })
const toggleDark = useToggle(isDark)
const confirm = useConfirm()

const showModal = ref(false)
const showLanguageDialog = ref(false)
const languageOptions = computed(() => [
  { name: t('common.languages.es'), value: 'es' },
  { name: t('common.languages.en'), value: 'en' },
])

const changeLanguage = (lang: 'es' | 'en') => {
  i18nStore.changeLanguage(lang)
  showLanguageDialog.value = false
}

const confirmLogout = () => {
  confirm.require({
    message: t('common.auth.sure_logout'),
    header: t('common.auth.confirm_logout'),
    icon: 'pi pi-exclamation-triangle',
    rejectClass: 'p-button-secondary p-button-outlined',
    rejectLabel: t('common.general.cancel'),
    acceptLabel: t('common.auth.logout'),
    acceptClass: 'p-button-danger',
    accept: () => {
      authStore.logout()
      showModal.value = false
    },
  })
}

const handleAction = (action: (() => void) | undefined) => {
  if (action) {
    action()
  }
}

const userInitials = computed(() => {
  return (
    authStore.extension?.number
      ?.split(' ')
      .map((n: string[]) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || 'U'
  )
})

const userName = computed(() => authStore.extension?.number || t('common.user.default_name'))
const extensionStatusLabel = computed(() =>
  locale.value === 'en'
    ? authStore.webphoneAvailable
      ? 'Connected'
      : 'Disconnected'
    : authStore.webphoneAvailable
      ? 'Conectada'
      : 'Desconectada',
)
const userNameWithStatus = computed(() => `${userName.value} - ${extensionStatusLabel.value}`)
const userEmail = computed(() => authStore.extension?.description || '')

const menuItems = computed(() => [
  {
    label: t('common.user.settings'),
    icon: 'pi pi-cog',
    action: () => {
      router.push('/settings')
      showModal.value = false
    },
  },
  {
    label: t('common.theme.dark'),
    icon: isDark.value ? 'pi pi-sun' : 'pi pi-moon',
    action: () => toggleDark(),
  },
  {
    label: t('common.languages.language'),
    icon: 'pi pi-globe',
    action: () => {
      showModal.value = false
      showLanguageDialog.value = true
    },
  },
  {
    separator: true,
  },
  {
    label: t('common.auth.logout'),
    icon: 'pi pi-sign-out',
    action: () => confirmLogout(),
  },
])
</script>

<template>
  <div class="relative">
    <div
      @click="showModal = true"
      class="cursor-pointer p-1 rounded-full hover:bg-gray-100 dark:hover:bg-(--p-surface-800) transition-colors"
    >
      <AppAvatar
        :label="userInitials"
        size="large"
        class="ring-2 bg-(--p-primary-color)! text-(--p-primary-contrast-color)!"
        :class="isDark ? 'ring-(--p-primary-color)/20' : 'ring-white/30'"
        style="width: 32px; height: 32px; font-size: 0.875rem"
      />
    </div>

    <Dialog
      v-model:visible="showModal"
      :modal="true"
      :closable="true"
      :dismissable-mask="true"
      class="w-80"
      :pt="{
        root: 'user-menu-modal',
      }"
    >
      <template #header>
        <div class="flex items-center gap-3 w-full">
          <AppAvatar
            :label="userInitials"
            size="normal"
            class="ring-2 bg-(--p-primary-color)! text-(--p-primary-contrast-color)!"
            :class="isDark ? 'ring-(--p-primary-color)/20' : 'ring-white/30'"
          />
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {{ userNameWithStatus }}
            </p>
            <p class="text-xs text-gray-500 dark:text-gray-400 truncate">
              {{ userEmail }}
            </p>
          </div>
        </div>
      </template>

      <!-- Menu items -->
      <div class="space-y-1 pt-2">
        <template v-for="(item, index) in menuItems" :key="index">
          <div v-if="item.separator" class="my-2 border-t border-gray-200 dark:border-gray-700" />
          <button
            v-else
            @click="handleAction(item.action)"
            class="flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors duration-200 w-full text-left text-gray-700 dark:text-white/90 hover:bg-gray-100 dark:hover:bg-(--p-surface-800)"
          >
            <i :class="item.icon" class="text-sm w-4" />
            <span>{{ item.label }}</span>
          </button>
        </template>
      </div>
    </Dialog>

    <Dialog
      v-model:visible="showLanguageDialog"
      :modal="false"
      :closable="true"
      :dismissable-mask="true"
      class="w-72"
      :header="t('common.languages.language')"
      :pt="{ root: 'language-floating-dialog' }"
    >
      <div class="flex flex-col gap-2">
        <button
          v-for="option in languageOptions"
          :key="option.value"
          type="button"
          class="flex items-center justify-between rounded-lg border border-(--p-surface-200) px-3 py-2 text-left text-sm transition hover:bg-(--p-surface-100) dark:border-(--p-surface-700) dark:text-white dark:hover:bg-(--p-surface-800)"
          :class="{
            'border-(--p-primary-color)! bg-(--p-primary-color)/10 text-(--p-primary-color)! dark:text-white!':
              i18nStore.language === option.value,
          }"
          @click="changeLanguage(option.value as 'es' | 'en')"
        >
          <span>{{ option.name }}</span>
          <i v-if="i18nStore.language === option.value" class="pi pi-check text-xs" />
        </button>
      </div>
    </Dialog>
  </div>
</template>

<style scoped>
:deep(.user-menu-modal) {
  max-width: 20rem;
}

:deep(.user-menu-modal .p-dialog-content) {
  padding: 0 1rem 1rem 1rem;
}

:deep(.user-menu-modal.p-dialog),
:deep(.user-menu-modal .p-dialog-header),
:deep(.user-menu-modal .p-dialog-content) {
  background: var(--p-surface-900);
  color: var(--p-text-color);
}

:deep(.language-floating-dialog.p-dialog),
:deep(.language-floating-dialog .p-dialog-header),
:deep(.language-floating-dialog .p-dialog-content) {
  background: var(--p-surface-900);
  color: var(--p-text-color);
}
</style>
